import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';

import { MaskService } from '../../../../../../../shared/services/mask.service';
import { montarFormParcela } from '../../venda-pagamento.form';
import { diasEntre } from '../../venda-pagamento.parcelas';

export interface VdParcelaDialogData {
    /** A linha da grade. O modal edita uma CÓPIA e só devolve no Salvar. */
    parcela: FormGroup;
    numero: number;
    total: number;

    /** A emissão do documento — é dela que os Dias são contados (§8.7.1). */
    dataEmissao: Date | string | null;

    /** NSU só existe no pedido: o orçamento não concilia financeiro. */
    ehOrcamento: boolean;
    isSomenteLeitura: boolean;
    /** DF10: o contas a receber exige NSU / observação na parcela. */
    isNsuObrigatorio?: boolean;
    isObservacaoObrigatoria?: boolean;
    mascaraDinheiro: Partial<NgxCurrencyConfig>;
}

/** O que volta no Salvar. */
export interface VdParcelaDialogResultado {
    dataVencimento: Date | string | null;
    dias: number | null;
    valor: number;
    nsu: string | null;
    observacao: string | null;
}

const OBSERVACAO_MAXIMO = 500;

/**
 * Modal "Editar parcela" (§8.7.1): vencimento, valor, NSU [PED] e observação.
 *
 * Edita uma CÓPIA da linha — Cancelar tem de descartar de verdade, e editando o FormGroup da
 * grade direto ele já teria mudado por baixo. Quem redistribui as demais parcelas quando o valor
 * muda é a grade, ao receber o resultado: o modal não conhece as irmãs.
 */
@Component({
    selector: 'app-vd-parcela-dialog',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatDatepickerModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        NgxCurrencyDirective,
    ],
    templateUrl: './vd-parcela-dialog.component.html',
    styleUrl: './vd-parcela-dialog.component.css',
})
export class VdParcelaDialogComponent {
    public readonly copia: FormGroup;
    public readonly observacaoMaximo = OBSERVACAO_MAXIMO;
    public readonly onDateInput = MaskService.onDateInput;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: VdParcelaDialogData,
        private dialogRef: MatDialogRef<VdParcelaDialogComponent, VdParcelaDialogResultado | undefined>,
        fb: FormBuilder,
    ) {
        this.copia = montarFormParcela(fb);
        this.copia.patchValue(data.parcela.getRawValue(), { emitEvent: false });

        // §8.7.1: vencimento obrigatório; valor obrigatório e maior que zero.
        this.copia.get('dataVencimento')?.setValidators(Validators.required);
        this.copia.get('valor')?.setValidators([Validators.required, Validators.min(0.01)]);
        // DF10: NSU e observação obrigatórios quando a configuração do contas a receber exige.
        if (data.isNsuObrigatorio && !data.ehOrcamento) this.copia.get('nsu')?.setValidators(Validators.required);
        if (data.isObservacaoObrigatoria) this.copia.get('observacao')?.setValidators(Validators.required);
        this.copia.updateValueAndValidity({ emitEvent: false });

        if (data.isSomenteLeitura) this.copia.disable({ emitEvent: false });

        // Alterar o vencimento recalcula os Dias a partir da emissão (§8.7.1).
        this.copia.get('dataVencimento')?.valueChanges.subscribe((venc) => {
            const emissao = this.comoData(this.data.dataEmissao);
            const vencimento = this.comoData(venc);

            if (!emissao || !vencimento) return;

            this.copia.get('dias')?.setValue(diasEntre(emissao, vencimento), { emitEvent: false });
        });
    }

    public get titulo(): string {
        return `Parcela ${this.data.numero}/${this.data.total}`;
    }

    public get observacaoTamanho(): number {
        return String(this.copia.get('observacao')?.value ?? '').length;
    }

    public cancelar(): void {
        this.dialogRef.close();
    }

    public salvar(): void {
        if (this.copia.invalid) {
            this.copia.markAllAsTouched();
            return;
        }

        const bruto = this.copia.getRawValue();

        this.dialogRef.close({
            dataVencimento: bruto.dataVencimento,
            dias: bruto.dias,
            valor: Number(bruto.valor) || 0,
            nsu: this.data.ehOrcamento ? null : (bruto.nsu || null),
            observacao: bruto.observacao || null,
        });
    }

    private comoData(valor: unknown): Date | null {
        if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
        if (typeof valor === 'string' && valor) {
            const d = new Date(valor);

            return isNaN(d.getTime()) ? null : d;
        }

        return null;
    }
}
